/**
 * ChatInterface Component
 * Chat with transcription context - Full screen optimized
 */

import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../Common/Button';

export function ChatInterface({ 
  messages, 
  loading, 
  onSendMessage, 
  onGetSummary,
  onBack,
  fileName 
}) {
  const [input, setInput] = useState('');
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;
    
    onSendMessage(input);
    setInput('');
  };

  const quickPrompts = [
    { label: '📋 Resumen ejecutivo', prompt: 'Genera un resumen ejecutivo' },
    { label: '✅ Tareas', prompt: 'Extrae las tareas accionables' },
    { label: '🎯 Decisiones', prompt: 'Cuáles fueron las decisiones tomadas' },
    { label: '📅 Fechas', prompt: 'Extrae fechas y plazos importantes' },
    { label: '❓ Dudas', prompt: 'Qué puntos quedaron sin resolver' },
    { label: '💰 Números', prompt: 'Resume datos numéricos mencionados' },
  ];

  return (
    <div className="flex flex-col h-[calc(100vh-140px)] min-h-[500px] bg-white rounded-xl border border-gray-200 shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-gray-200 bg-gray-50 rounded-t-xl">
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold text-gray-900 text-lg">💬 Chat con IA</h3>
          <p className="text-xs text-gray-500 truncate">{fileName}</p>
        </div>
        <Button onClick={onBack} variant="secondary" size="small">
          ← Volver
        </Button>
      </div>

      {/* Quick prompts */}
      <div className="flex gap-2 p-3 border-b border-gray-200 overflow-x-auto bg-white">
        {quickPrompts.map((prompt, i) => (
          <button
            key={i}
            onClick={() => onSendMessage(prompt.prompt)}
            disabled={loading}
            className="flex-shrink-0 px-3 py-1.5 text-xs bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 disabled:opacity-50 transition-colors"
          >
            {prompt.label}
          </button>
        ))}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 bg-gray-50">
        {messages.map((msg, i) => (
          <div
            key={i}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[90%] sm:max-w-[80%] rounded-2xl px-4 py-3 shadow-sm ${
                msg.role === 'user'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-800 border border-gray-200'
              }`}
            >
              <div className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</div>
              <div className={`text-xs mt-2 ${msg.role === 'user' ? 'text-primary-200' : 'text-gray-400'}`}>
                {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          </div>
        ))}
        
        {loading && (
          <div className="flex justify-start">
            <div className="bg-white border border-gray-200 rounded-2xl px-4 py-3 shadow-sm">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:0.2s]" />
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:0.4s]" />
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} className="p-4 border-t border-gray-200 bg-white rounded-b-xl">
        <div className="flex gap-2 max-w-4xl mx-auto">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Escribe tu pregunta sobre la reunión..."
            disabled={loading}
            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50 text-base"
          />
          <Button 
            type="submit" 
            disabled={loading || !input.trim()} 
            variant="primary"
            className="px-6"
          >
            Enviar
          </Button>
        </div>
        <p className="mt-2 text-xs text-gray-400 text-center max-w-4xl mx-auto">
          La IA solo tiene acceso a la transcripción de esta reunión
        </p>
      </form>
    </div>
  );
}
